import TableOnly from "components/DataTable/TableOnly";
import PaginationWithState from "components/Pagination/PaginationWithState";
import PurchaseReportPdfButton from "components/Pdfs/PurchaseReportPdf/PurchaseReportPdfButton";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import * as XLSX from "xlsx";

export default function PurchaseHistory() {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();

  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear() - 1, now.getMonth() - 1, 2)
      .toISOString()
      .slice(0, 10)
      .replace("T", " ");
  });

  const [endDate, setEndDate] = useState(
    new Date().toISOString().slice(0, 10).replace("T", " "),
  );

  const [sortBy, setSortBy] = useState("none");
  const [sortDirection, setSortDirection] = useState("asc");

  const [showDateFilter, setShowDateFilter] = useState(false);
  const [purchaseReport, setPurchaseReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState(null);

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const [filterBy, setFilterBy] = useState("all");
  const [exportLoading, setExportLoading] = useState(false);

  /*   All Functions
   ********************************************* */
  // Fetch all data for export
  const fetchAllDataForExport = async () => {
    try {
      const endDateObj = new Date(endDate);
      const endDateString = endDateObj.toISOString().slice(0, 10);

      const { data } = await API.post(
        "/manufacturer-purchases-report-with-pagination?page=1&limit=9999",
        {
          startDate: startDate,
          endDate: endDateString,
          sortBy: sortBy,
          sortDirection: sortDirection,
          query: searchTerm,
          filterBy: filterBy,
        },
        {
          headers: authHeader(),
        },
      );

      if (data?.success) {
        return data?.data || [];
      }
      return [];
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
      return [];
    }
  };

  //get Purchase Report
  const getPurchaseReport = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);

        const usedQuery =
          clearSearch ? ""
          : typeof query !== "undefined" ? query
          : "";
        const usedPage = page ?? pageNumber;

        // increment 1 to endDate
        const endDateObj = new Date(endDate);

        // get only date frm endDateObj not time
        const endDateString = endDateObj.toISOString().slice(0, 10);

        const { data } = await API.post(
          "/manufacturer-purchases-report-with-pagination?page=" +
            usedPage +
            "&limit=" +
            pageLimit,
          {
            startDate: startDate,
            endDate: endDateString,
            sortBy: sortBy,
            sortDirection: sortDirection,
            query: usedQuery,
            filterBy: filterBy,
          },
          {
            headers: authHeader(),
          },
        );
        if (data?.success) {
          setPurchaseReport(data?.data);
          setTotalPages(data?.totalPages || 0);
        }
      } catch (err) {
        dispatch(
          setNotification({
            message: err.message,
            type: "error",
          }),
        );
      } finally {
        setLoading(false);
      }
    },
    [
      pageNumber,
      pageLimit,
      sortBy,
      sortDirection,
      startDate,
      endDate,
      filterBy,
    ],
  );

  // Getting 12 Hours Time from SQL timestamp
  const convertSqlTimestampToDate = (sqlTime) => {
    const timestamp = sqlTime;
    const date = new Date(timestamp);
    const time = date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    return time;
  };

  const getIssueQty = (item) => {
    let issueQty = item?.m_issues
      ?.reduce((acc, item) => acc + item.qty, 0)
      ?.toFixed(3);

    if (issueQty === "0.000") {
      return "0";
    }
    return issueQty || 0;
  };

  const downloadExcel = async () => {
    try {
      setExportLoading(true);
      const dataToExport = await fetchAllDataForExport();

      if (dataToExport.length === 0) {
        dispatch(
          setNotification({
            message: "No data found to export",
            type: "info",
          }),
        );
        return;
      }

      const excelData = dataToExport.map((item) => ({
        "Purchase Date":
          item?.purchaseDate ?
            convertSqlTimestampToDate(item?.purchaseDate)
          : "-",
        "T-ID": item?.tid || "-",
        "Lot #": item?.lotId || "-",
        Material: item?.styleId || "-",
        Color: item?.m_materialList?.color || "-",
        Vendor: item?.vendorName || "-",
        "Purchased Qty": item?.purchasedQty || 0,
        "Unit Price": item?.unitPrice?.toFixed(2) || 0,
        "Lot Total Price": item?.lotTotalPrice?.toFixed(2) || 0,
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Purchase History");
      XLSX.writeFile(
        workbook,
        `purchase-history-${new Date().toISOString().slice(0, 10)}.xlsx`,
      );

      dispatch(
        setNotification({
          message: "Export successful",
          type: "success",
        }),
      );
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        }),
      );
    } finally {
      setExportLoading(false);
    }
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      getPurchaseReport({
        page: pageNumber,
        query: searchTerm,
      });
    }, 50);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageLimit]);

  // useEffect(() => {
  //   if (purchaseReport.length === 0) return;

  //   let sortedData = [...purchaseReport];

  //   if (sortBy !== "none") {
  //     sortedData.sort((a, b) => {
  //       let fieldA = a[sortBy];
  //       let fieldB = b[sortBy];

  //       if (sortDirection === "asc") {
  //         return fieldA < fieldB ? -1 : fieldA > fieldB ? 1 : 0;
  //       } else {
  //         return fieldA > fieldB ? -1 : fieldA < fieldB ? 1 : 0;
  //       }
  //     });
  //   }

  //   setPurchaseReport(sortedData);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [sortBy, sortDirection]);

  /*   Table functions
   ********************************************* */
  const btn1 = () => (
    <div className="d-flex justify-content-end mb-0">
      <Dropdown>
        <Dropdown.Toggle
          className="px-x ms-1"
          variant="primary"
          id="dropdown-basic"
          disabled={exportLoading}
        >
          {exportLoading ? "Exporting..." : "Reports"}
        </Dropdown.Toggle>

        <Dropdown.Menu className="custom-shadow-sm">
          <Dropdown.Item
            onClick={(e) => {
              e.stopPropagation();
            }}
            as="div"
            className="px-2 m-0 "
          >
            <PurchaseReportPdfButton
              startDate={startDate}
              endDate={endDate}
              sortBy={sortBy}
              sortDirection={sortDirection}
              query={searchTerm}
              filterBy={filterBy}
              btnTitle={"Export PDF"}
              btnClasses={"btn w-100"}
              btnColor={"text-start btn"}
            />
          </Dropdown.Item>
          <Dropdown.Item
            onClick={(e) => {
              e.stopPropagation();
            }}
            as="div"
            className="px-2 m-0"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                downloadExcel();
              }}
              disabled={exportLoading}
              className="btn w-100 text-start btn-success"
            >
              {exportLoading ? "Exporting..." : "Export Excel"}
            </button>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );

  const buttons = () => (
    <div className=" d-flex justify-content-end ">
      <div className="d-flex align-items-center">
        <div className="me-2 text-nowrap">Sort By:</div>
        <select
          className="form-select form-select-sm"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="none">None</option>
          <option value="purchaseDate">Date</option>
          <option value="tid">T-ID</option>
          <option value="vendorName">Vendor</option>
          <option value="styleId">Material</option>
          <option value="m_materialList.color">Color</option>
          <option value="lotId">Lot #</option>
          <option value="purchasedQty">Purchased Qty</option>
          <option value="unitPrice">Unit Price</option>
          <option value="lotTotalPrice">Lot Total Price</option>
        </select>
      </div>{" "}
      <div className="d-flex align-items-center ms-2">
        <div className="me-2 text-nowrap">Direction:</div>
        <select
          className="form-select form-select-sm"
          value={sortDirection}
          onChange={(e) => setSortDirection(e.target.value)}
        >
          <option value="asc">Ascending</option>
          <option value="desc">Descending</option>
        </select>
      </div>
      {/* From */}
      <div className="row ">
        <div className="col-3  d-flex justify-content-start ms-1 align-items-center ">
          <div className="">From</div>
        </div>
        <div className="col-8 px-0">
          <input
            className="form-control"
            value={startDate || ""}
            onChange={(e) => setStartDate(e.target.value)}
            type={"date"}
          />
        </div>
      </div>
      {/* To */}
      <div className="row">
        <div className="col-3  d-flex justify-content-start ms-1 align-items-center ">
          <div className="">TO</div>
        </div>
        <div className="col-8 px-0">
          <input
            className="form-control"
            value={endDate || ""}
            onChange={(e) => setEndDate(e.target.value)}
            type={"date"}
          />
        </div>
      </div>
      <div className="d-flex justify-content-end ">
        <button
          onClick={() => {
            getPurchaseReport({
              page: 1,
              query: searchTerm,
            });
            setShowDateFilter(!showDateFilter);
          }}
          className="btn btn-success mx-2"
        >
          Filter
        </button>
      </div>
    </div>
  );

  const table_head = [
    { value: "purchaseDate", name: "Purchase date" },
    { value: "tid", name: "T-ID" },
    { value: "lotId", name: "Lot #" },
    { value: "styleId", name: "Material" },
    { value: "m_materialList.color", name: "Color" },
    { value: "vendorName", name: "Vendor" },
    { value: "purchasedQty", name: "Purchased Qty" },
    { value: "unitPrice", name: "Unit Price" },
    { value: "lotTotalPrice", name: "Lot Total Price" },
    { value: "", name: "Actions" },
  ];

  const table_body = (item, index) => (
    <tr className="align-middle py-1" key={item.id}>
      <td>
        {item?.purchaseDate ?
          convertSqlTimestampToDate(item?.purchaseDate)
        : "-"}
      </td>
      <td className="align-middle py-1">{item?.tid}</td>
      <td className="align-middle py-1">{item?.lotId}</td>
      <td className="align-middle py-1">{item?.styleId}</td>
      <td className="align-middle py-1">{item?.m_materialList?.color}</td>
      <td className="align-middle py-1">{item?.vendorName}</td>
      <td className="align-middle py-1">{item?.purchasedQty}</td>

      <td className="align-middle py-1">{item?.unitPrice?.toFixed(2)}</td>
      <td className="align-middle py-1">
        {(item?.unitPrice * item?.purchasedQty)?.toFixed(2)}
      </td>
      <td className="align-middle py-1">
        <button
          onClick={() => {
            setSelectedInfo(item);
            setShowInfoModal(true);
          }}
          className="btn btn-sm btn-info"
        >
          Qty Info
        </button>
        <Link
          to={
            "/manufacturer/material-stock?tab=material-purchase&materialId=" +
            item?.materialId +
            "&page=1&limit=10&tid=" +
            item?.tid
          }
          className="btn btn-sm btn-success ms-1"
        >
          View
        </Link>
      </td>
    </tr>
  );

  return (
    <div className="mt-3 ps-2 pe-3">
      {loading && <Spinner />}
      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div>
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex">
          {buttons()}
          <div>
            <select
              value={filterBy}
              className="form-select me-1 border-info"
              onChange={(e) => setFilterBy(e.target.value)}
              name="filterBy"
              id="filterBy"
            >
              <option value="all">All</option>
              <option value="purchaseDate">Date</option>
              <option value="tid">T-ID</option>
              <option value="vendorName">Vendor</option>
              <option value="styleId">Material</option>
              <option value="m_materialList.color">Color</option>
              <option value="lotId">Lot #</option>
              <option value="purchasedQty">Purchased Qty</option>
              <option value="unitPrice">Unit Price</option>
              <option value="lotTotalPrice">Lot Total Price</option>
            </select>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getPurchaseReport({ query: searchTerm, page: 1 });
              } else {
                getPurchaseReport({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search by name..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getPurchaseReport({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          {btn1()}
        </div>
      </div>
      <div className="horizontal-scrollable d-flex flex-grow-1">
        <TableOnly
          table_head={table_head}
          actionsOnEnd={true}
          disableSorting={true}
          table_body={table_body}
          table_data={purchaseReport}
        />
      </div>
      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>
      {/*   Qty Info Modal
       ********************************************* */}
      <Modal show={showInfoModal} onHide={() => setShowInfoModal(false)}>
        <Modal.Body
          className="shadow-lg bg-white px-3 "
          style={{ borderRadius: 15 }}
        >
          <div className="">
            <h4>Purchase Details</h4>
          </div>
          <hr className="mt-0" />
          {/* Original/Purchased Qty */}
          <div className="row mb-2">
            <div className="col-5 fw-bold">Purchased Qty:</div>
            <div className="col-2">:</div>
            <div className="col-5">
              {selectedInfo?.purchasedQty?.toFixed(3) || 0}
            </div>
          </div>
          {/* Return Qty */}
          <div className="row mb-2">
            <div className="col-5 fw-bold">Return Qty:</div>
            <div className="col-2">:</div>
            <div className="col-5">
              {selectedInfo?.returnQty?.toFixed(3) || 0}
            </div>
          </div>
          {/* Issue Qty */}
          <div className="row mb-2">
            <div className="col-5 fw-bold">Issue Qty:</div>
            <div className="col-2">:</div>
            <div className="col-5">{getIssueQty(selectedInfo)}</div>
          </div>
          {/* Available Qty */}
          <div className="row mb-2">
            <div className="col-5 fw-bold">Available Qty:</div>
            <div className="col-2">:</div>
            <div className="col-5">{selectedInfo?.qty?.toFixed(3) || 0} </div>
          </div>
          <hr />
          {/* Buttons */}
          <div className="d-flex justify-content-end">
            <button
              onClick={() => {
                setShowInfoModal(false);
              }}
              className="btn btn-secondary btn-sm"
              type="button"
            >
              Close
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
